/**
 * OC FAB Widget Component
 * Floating Action Button widget with sliding chat panel
 * Handles authentication, panel toggle, and session management
 */

import { Alert } from '@hl/ui-components';
import { FC, useEffect, useMemo, useRef, useState } from 'react';
import { ThemeProvider } from 'styled-components';

import { OcChatConfig } from '../../types/oc';
import { OcFabWidgetOptions } from '../../types/oc';
import { OcSkeletonDefs } from '../Loading/SkeletonSVG';
import { ErrorWrapper } from '../TranscriptBox/TranscriptMessages.styled';
import {
  AuthState,
  OcChatPanel,
  OcChatPanelLoadingShell,
} from './components/ChatPanelContent/OcChatPanel';
import { OcFabWidgetTrigger } from './components/FabButton/OcFabWidgetTrigger';
import { NotificationProvider } from './components/Notification/NotificationProvider';
import {
  ConfigValidationError,
  validateOcChatConfig,
} from './config/configValidator';
import { getOcTheme, IOcAppTheme } from './config/ocTheme';
import {
  OcFabWidgetOptionsProvider,
} from './contexts/OcFabWidgetOptionsContext';
import { useEffectivePrivacyConfig } from './hooks/useEffectivePrivacyConfig';
import { hasAuthHeaders } from './services/ocApiClient';
import { checkOcAuth } from './services/ocEndpoints';
import { SessionManagerProvider } from './sessionManagement/SessionManagerContext';
import { OcGlobalStyleWrapper } from './styles/OcGlobalStyle';
import { ensureInterStylesheet } from './utils/ensureInterStylesheet';

export interface OcFabWidgetProps {
  config: OcChatConfig;
  options?: OcFabWidgetOptions;
}

/**
 * OcFabWidget Component
 * Main FAB widget with authentication and session management
 */
export const OcFabWidget: FC<OcFabWidgetProps> = ({ config, options }) => {
  // Ensure the Inter font is available when the widget is mounted as a React component.
  // (The external embed API also calls this, but React usage may not.)
  useEffect(() => {
    ensureInterStylesheet();
  }, []);

  const [authState, setAuthState] = useState<AuthState>('checking');
  const [userId, setUserId] = useState<string>('');
  const [error, setError] = useState<Error | null>(null);
  const [isChatOpen, setIsChatOpen] = useState(options?.autoOpen ?? false);
  const hasChatBeenOpen = useRef(options?.autoOpen ?? false);
  const originToolId =
    config?.originToolId ||
    config?.aiAssistantKey ||
    '00000000-0000-0000-0000-000000000000';

  // Determine if we're in preview mode
  const isPreviewMode = config.mode === 'preview';
  const previewState = options?.previewState;
  const usePreviewButton = isPreviewMode && !!previewState?.usePreviewButton;

  // Ref keeps the latest options available inside effects without
  // making the effects re-run when the embedder passes a new object.
  const optionsRef = useRef(options);
  optionsRef.current = options;

  // Stabilize config so downstream hooks/effects only re-run when fields
  // that affect API identity actually change.
  // isPrivate/isAnonymous are intentionally excluded: they are populated
  // downstream by useEffectivePrivacyConfig after Crest responds.
  // TODO: Manually enumerating fields is fragile -- new OcChatConfig fields must be added here
  // (or intentionally excluded). Consider a utility or documenting the exclusion list instead.
  const stableConfig = useMemo<OcChatConfig>(
    () => ({
      apiBaseUrl: config.apiBaseUrl,
      tenantId: config.tenantId,
      aiAssistantKey: config.aiAssistantKey,
      loginUrl: config.loginUrl,
      originToolId: config.originToolId,
      mode: config.mode,
      iamKey: config.iamKey,
      HL_Test: config.HL_Test,
    }),
    [
      config.apiBaseUrl,
      config.tenantId,
      config.aiAssistantKey,
      config.loginUrl,
      config.originToolId,
      config.mode,
      config.iamKey,
      config.HL_Test,
    ],
  );

  // Privacy comes from Crest only (GetPrivacySettings); widget cannot set or receive it elsewhere.
  // Skip the call when no auth headers exist — unauthenticated users see the login screen
  // and privacy will be fetched once they log in (page reload).
  const isAuthAvailable = hasAuthHeaders(stableConfig);
  const privacyResult = useEffectivePrivacyConfig(stableConfig, isAuthAvailable);

  // Treat privacy load failure as a fatal error — but only once we know the user is
  // authenticated.  While auth is still checking, a 401 from the privacy endpoint
  // may simply mean the user isn't logged in; reporting it as an error would hide
  // the login screen.
  useEffect(() => {
    if (isPreviewMode || privacyResult.status !== 'error') return;
    if (authState !== 'authenticated') return;
    setError(privacyResult.error);
    setAuthState('error');
    optionsRef.current?.onError?.(privacyResult.error);
  }, [privacyResult, isPreviewMode, authState]);

  // Check authentication on mount (skip in preview mode)
  useEffect(() => {
    const opts = optionsRef.current;

    // In preview mode, set auth state based on previewState
    if (isPreviewMode) {
      if (!previewState) {
        const error = new Error('Preview mode requires previewState to be provided');
        setError(error);
        setAuthState('error');
        opts?.onError?.(error);
        return;
      }

      // Set auth state based on previewState.authState
      if (previewState.authState === 'authenticated') {
        setUserId('preview-user-id');
        setAuthState('authenticated');
        if (opts?.onReady) {
          setTimeout(opts.onReady, 0);
        }
      } else {
        setAuthState('unauthenticated');
        opts?.onAuthRequired?.();
      }
      return;
    }

    // Production mode - check authentication normally
    const checkAuth = async () => {
      try {
        validateOcChatConfig(stableConfig);

        // Only check authentication if headers are available
        // If no iamKey and no cookies, skip the API call and set unauthenticated state
        if (!hasAuthHeaders(stableConfig)) {
          setAuthState('unauthenticated');
          optionsRef.current?.onAuthRequired?.();
          return;
        }

        const authResponse = await checkOcAuth(stableConfig);

        if (authResponse.isAuthenticated) {
          if (!authResponse.userId) {
            throw new Error('Auth response missing userId');
          }
          setUserId(authResponse.userId);
          setAuthState('authenticated');

          if (optionsRef.current?.onReady) {
            setTimeout(optionsRef.current.onReady, 0);
          }
        } else {
          setAuthState('unauthenticated');
          optionsRef.current?.onAuthRequired?.();
        }
      } catch (err) {
        const error = err instanceof Error ? err : new Error(String(err));
        setError(error);
        setAuthState('error');
        optionsRef.current?.onError?.(error);
      }
    };

    checkAuth();
  }, [stableConfig, isPreviewMode, previewState]);

  // Build theme based on themeMode and useSVGBackground
  const themeMode = options?.themeMode || 'light';
  const useSVGBackground = options?.useSVGBackground !== false; // default true
  const baseTheme = getOcTheme(themeMode, useSVGBackground);

  const theme: IOcAppTheme = options?.theme
    ? {
      ...baseTheme,
      color: {
        ...baseTheme.color,
        ...options.theme.color,
      },
      font: {
        size: {
          ...baseTheme.font.size,
          ...options.theme.font?.size,
        },
        weight: {
          ...baseTheme.font.weight,
          ...options.theme.font?.weight,
        },
      },
    }
    : baseTheme;

  // Handle FAB click
  const handleFabClick = () => {
    const newState = !isChatOpen;
    setIsChatOpen(newState);
    hasChatBeenOpen.current = true;

    if (options?.onFabClick) {
      options.onFabClick();
    }

    if (options?.onPanelToggle) {
      options.onPanelToggle(newState);
    }
  };

  // Handle panel close
  const handlePanelClose = () => {
    setIsChatOpen(false);

    if (options?.onPanelToggle) {
      options.onPanelToggle(false);
    }
  };

  // Error state (configuration errors only - don't show FAB)
  if (
    authState === 'error' &&
    error &&
    error instanceof ConfigValidationError
  ) {
    const errorMessage = `Configuration Error: ${error.message}`;

    return (
      <>
        <OcGlobalStyleWrapper />
        <ThemeProvider theme={theme}>
          <OcSkeletonDefs />
          <ErrorWrapper>
            {/* @ts-expect-error HL components is on an older react version where children is implicitly included */}
            <Alert danger={true}>{errorMessage}</Alert>
          </ErrorWrapper>
        </ThemeProvider>
      </>
    );
  }

  return (
    <>
      <OcGlobalStyleWrapper />
      <ThemeProvider theme={theme}>
        <OcSkeletonDefs />
          <OcFabWidgetOptionsProvider options={options}>
            <NotificationProvider>
              {/* Trigger button - always visible (even when checking/unauthenticated) */}
              <OcFabWidgetTrigger
                usePreviewButton={usePreviewButton}
                onClick={handleFabClick}
              />

              {/* Chat Panel - lazy loaded after first open.
                  Unauthenticated users bypass the privacy gate: privacy settings are
                  irrelevant until login, and OcChatPanel already shows the login screen.
                  Auth must be resolved before mounting SessionManagerProvider so that
                  userId is available for cross-user session validation. */}
              {hasChatBeenOpen.current &&
                (authState !== 'checking' &&
                (privacyResult.status === 'success' || authState === 'unauthenticated') ? (
                  <SessionManagerProvider
                    config={privacyResult.config}
                    originToolId={originToolId}
                    isAuthenticated={authState === 'authenticated'}
                    userId={userId}
                    previewState={previewState}
                  >
                    <OcChatPanel
                      config={privacyResult.config}
                      userId={userId}
                      authState={authState}
                      error={error}
                      theme={theme}
                      isOpen={isChatOpen}
                      onClose={handlePanelClose}
                    />
                  </SessionManagerProvider>
                ) : (
                  <OcChatPanelLoadingShell
                    theme={theme}
                    isOpen={isChatOpen}
                    onClose={handlePanelClose}
                    error={privacyResult.status === 'error' ? privacyResult.error : null}
                  />
                ))}
            </NotificationProvider>
          </OcFabWidgetOptionsProvider>
      </ThemeProvider>
    </>
  );
};

export default OcFabWidget;
